import {
  Color,
  DirectionalLight,
  Fog,
  HemisphereLight,
  Matrix4,
  type Mesh,
  MeshBasicMaterial,
  NoToneMapping,
  Object3D,
  PCFShadowMap,
  Scene,
  SRGBColorSpace,
  Vector2,
  Vector3,
  WebGLRenderer,
} from 'three';
import { ERAS } from '../config/eras';
import type { GeneratedCircuit } from '../track/generate';
import { planScenery, type SceneryPlan } from '../track/scenery';
import { ChaseCamera, type CameraTarget } from './camera';
import { SOFTWARE_GL } from './sharpness';
import { buildCar } from './car/carModel';
import { CarRig, type CarVisual } from './car/carRig';
import { makeHills } from './hills';
import { LOOKS, type SeaPlan, type SettingId, useLook } from './look';
import { buildModifierVisuals, type ModifierVisuals } from './modifiers';
import { PaperOverlay } from './post';
import { buildScenery, type SceneryRender } from './scenery/build';
import { makeSky } from './sky';
import type { TrackData } from '../track/types';
import { Terrain } from './terrain';
import { posterTexture } from './textures';
import { buildDevOverlays, buildTrackMeshes, type DevOverlays } from './trackMesh';
import { buildCrossing } from './crossing';
import { circuitDate } from '../track/calendar';
import { buildLessonOverlay, type LessonOverlay, type LessonOverlayEvent } from './lessonOverlay';

export interface WorldOptions {
  renderer: WebGLRenderer;
  circuit: GeneratedCircuit;
  dateLabel: string;
  lowQuality?: boolean;
  /** The driving lesson's braking events: given (even empty), the world paints the lesson's road overlay, hidden until asked. */
  lessonEvents?: LessonOverlayEvent[];
  /** Where in Italy (look.ts): Tuscany unless told otherwise. Only the look: nothing the race runs on. */
  setting?: SettingId;
}

export interface FrameInfo {
  dt: number;
  player: CarVisual;
  camera: CameraTarget;
  ghost: CarVisual | null;
  /** More ghosts (watching a podium: the other two laps). */
  ghosts?: CarVisual[];
  /** Which of those are yesterday's winner's lap (drawn in gold). */
  ghostsGold?: boolean[];
  /** The title card's slow orbit round the car. */
  orbit: boolean;
  excitement: number;
}

export const LIGHT = {
  sunElevationDeg: 17,
  /** The light comes from a bit further right than the painted sun disc, so cars are side-lit, not backlit. */
  lightYawOffset: -0.46,
  lightElevationDeg: 21,
  /** Irradiances in units of pi (three.js Lambert divides by pi). */
  sun: 0.74,
  hemi: 0.5,
  sunColor: '#ffe2b2',
  skyColor: '#ffe9c8',
  groundColor: '#e39a62',
  fogNear: 260,
  fogFar: 3600,
  /** Sun disc radius as a fraction of the viewport height. */
  sunRadius: 0.088,
};

export class World {
  readonly renderer: WebGLRenderer;
  readonly scene = new Scene();
  readonly chase = new ChaseCamera();
  readonly plan: SceneryPlan;
  readonly terrain: Terrain;
  readonly dev: DevOverlays;
  /** The driving lesson's braking ribbons and racing line (null without lesson events). */
  readonly lesson: LessonOverlay | null;
  private sun: DirectionalLight;
  private hemi: HemisphereLight;
  private sunDir: Vector3;
  private lightDir: Vector3;
  private sky: ReturnType<typeof makeSky>;
  private scenery: SceneryRender;
  private mods: ModifierVisuals;
  private overlay = new PaperOverlay();
  player: CarRig;
  ghost: CarRig;
  /** Yesterday's winner's car made of gold: the car on screen (yours, or a lap watched), and the ghost. */
  private gold = { car: false, ghost: false };
  /** The same for each extra ghost (laps watched together). */
  private extraGold: boolean[] = [];
  /** Extra ghosts, made the first time they're needed. */
  private extra: CarRig[] = [];
  /** How visible each extra ghost was last frame (0 to 1), for their name flags. */
  readonly extraOpacity: number[] = [];
  private look: (typeof ERAS)[keyof typeof ERAS]['look'];
  /** The day's setting (look.ts). */
  readonly setting: SettingId;
  private time = 0;
  postEnabled = true;
  /** Filming mode only (src/film.ts): moves the camera after the chase camera has, and draws just the car, white on black. */
  cameraHook: ((dt: number) => void) | null = null;
  matte = false;
  private matteMaterial: MeshBasicMaterial | null = null;

  constructor(o: WorldOptions) {
    const { circuit } = o;
    this.renderer = o.renderer;

    // The scenery is the circuit's: seeded by the date it was made for (calendar.ts), so a swapped day keeps it.
    const made = circuitDate(circuit.date);
    const seed = hashSeed(made + '|' + circuit.salt);
    this.plan = planScenery(circuit.track, made + '|' + circuit.salt);
    // The day's setting: everything built from here on is dressed in it (a sea, if it has one, on the far side
    // from the town).
    const settingId = o.setting ?? 'tuscany';
    const seaLook = LOOKS[settingId].sea;
    const sea = seaLook ? planSea(circuit.track, this.plan, seaLook.level) : null;
    const L = useLook(settingId, sea);
    this.setting = settingId;
    this.terrain = new Terrain(circuit.track, this.plan, seed);

    // Sun: low, golden, from ahead-right of the start straight.
    const dirOf = (elDeg: number, yaw: number): Vector3 => {
      const el = (elDeg * Math.PI) / 180;
      return new Vector3(Math.cos(el) * Math.cos(yaw), Math.sin(el), -Math.cos(el) * Math.sin(yaw)).normalize();
    };
    const lt = L.light;
    this.sunDir = dirOf(lt.sunElevationDeg, this.plan.sunYaw);
    this.lightDir = dirOf(lt.lightElevationDeg, this.plan.sunYaw + LIGHT.lightYawOffset);

    this.scene.background = L.sky.horizon.clone();
    this.scene.fog = new Fog(L.sky.horizon.clone(), lt.fogNear, lt.fogFar);
    this.sky = makeSky(this.sunDir);
    this.scene.add(this.sky);
    const hillSeed = seed % 1000;
    this.scene.add(makeHills(this.plan.bounds.cx, -this.plan.bounds.cy, hillSeed, L.hills({ seed: hillSeed, sunYaw: this.plan.sunYaw, sea }), L.volcano ? { sunYaw: this.plan.sunYaw, ...L.volcano } : null, L.sky));

    this.hemi = new HemisphereLight(new Color(lt.skyColor), new Color(lt.groundColor), lt.hemi * Math.PI);
    this.scene.add(this.hemi);
    this.sun = new DirectionalLight(new Color(lt.sunColor), lt.sun * Math.PI);
    this.sun.castShadow = true;
    const size = o.lowQuality ? 1024 : 2048;
    this.sun.shadow.mapSize.set(size, size);
    const S = 46;
    const cam = this.sun.shadow.camera;
    cam.left = -S;
    cam.right = S;
    cam.top = S;
    cam.bottom = -S;
    cam.near = 1;
    cam.far = 1200;
    // Small biases, so the car's shadow starts at its tyres: at this low sun a 5 cm normal bias slid it
    // 13 cm away from them (the car looked like it hovered).
    this.sun.shadow.bias = -0.0003;
    this.sun.shadow.normalBias = 0.02;
    this.scene.add(this.sun);
    this.scene.add(this.sun.target);

    this.scene.add(this.terrain.build(o.lowQuality ? 20 : 14));
    this.scene.add(buildTrackMeshes(circuit.track, circuit.line.offset));
    if (circuit.track.crossing) this.scene.add(buildCrossing(circuit.track, circuit.track.crossing));
    this.scenery = buildScenery(this.plan, this.terrain, seed, o.lowQuality);
    this.scene.add(this.scenery.group);
    const poster = posterTexture(circuit.name, o.dateLabel);
    this.mods = buildModifierVisuals(circuit.spec, this.plan, this.terrain, poster);
    this.scene.add(this.mods.group);

    this.dev = buildDevOverlays(circuit.track, circuit.line.x, circuit.line.y);
    for (const g of Object.values(this.dev)) this.scene.add(g as Object3D);
    this.lesson = o.lessonEvents ? buildLessonOverlay(circuit.track, circuit.line, o.lessonEvents) : null;
    if (this.lesson) this.scene.add(this.lesson.group);

    // The day's car; the ghost is the same car in its ghost colours.
    const look = ERAS[circuit.era].look;
    this.look = look;
    this.player = new CarRig(buildCar(look));
    this.scene.add(this.player.model.root);
    this.ghost = new CarRig(buildCar(look, true));
    this.ghost.model.root.visible = false;
    this.ghost.model.root.traverse((c) => (c.castShadow = false));
    this.scene.add(this.ghost.model.root);
  }

  /**
   * Yesterday's winner's car is made of gold today: `car`, the one on screen (the champion's own, or their lap
   * being watched); `ghost`, the ghost raced against (the champion's own best, or their lap as a rival).
   */
  setGold(car: boolean, ghost: boolean = car): void {
    if (car !== this.gold.car) this.player = this.swapRig(this.player, false, car);
    if (ghost !== this.gold.ghost) this.ghost = this.swapRig(this.ghost, true, ghost);
    this.gold = { car, ghost };
  }

  /** The same car rebuilt, gold or not, in the old one's place. */
  private swapRig(old: CarRig, ghost: boolean, gold: boolean): CarRig {
    const rig = new CarRig(buildCar(this.look, ghost, gold));
    rig.model.root.visible = old.model.root.visible;
    if (ghost) rig.model.root.traverse((c) => (c.castShadow = false));
    this.scene.remove(old.model.root);
    old.model.root.traverse((o) => (o as Mesh).geometry?.dispose());
    for (const m of old.model.materials) m.dispose();
    this.scene.add(rig.model.root);
    return rig;
  }

  get grandstandPos(): [number, number] {
    return this.scenery.grandstandAt;
  }

  resize(w: number, h: number): void {
    this.renderer.setSize(w, h, false);
    const cam = this.chase.camera;
    cam.aspect = w / h;
    this.chase.aspect = w / h;
    cam.updateProjectionMatrix();
    const pr = this.renderer.getPixelRatio();
    this.overlay.setSize(w * pr, h * pr);
  }

  /**
   * A ghost car in its own colours; it fades out as it comes between the camera
   * and the car being followed. Returns how visible it is (0 to 1).
   */
  private showGhost(rig: CarRig, v: CarVisual | null, player: CarVisual, dt: number): number {
    if (!v) {
      rig.model.root.visible = false;
      return 0;
    }
    rig.update(v, dt);
    const cam = this.chase.camera.position;
    const d = Math.hypot(v.x - cam.x, v.z - cam.z);
    const dPlayer = Math.hypot(player.x - cam.x, player.z - cam.z);
    let o = Math.max(0, Math.min(1, (d - 5.5) / 7));
    if (d < dPlayer + 1) o = Math.min(o, 0.2);
    rig.model.root.visible = o > 0.02;
    rig.setOpacity(o);
    return o;
  }

  /**
   * Get every material ready for the graphics chip now (while loading), hidden ones included: the ghost
   * car, the lesson's road markings. Otherwise each one is prepared the first time it's drawn, a hitch
   * mid-race (the best lap's ghost appeared just after the finish line).
   */
  precompile(): void {
    const hidden: Object3D[] = [];
    this.scene.traverse((o) => {
      if (!o.visible) {
        hidden.push(o);
        o.visible = true;
      }
    });
    try {
      this.renderer.compile(this.scene, this.chase.camera);
      this.renderer.compile(this.overlay.scene, this.overlay.camera);
      // And draw once with them all (unseen: the loading poster covers it): the browser only waits for a
      // program to finish preparing the first time it's drawn with.
      this.renderer.render(this.scene, this.chase.camera);
      this.renderer.render(this.overlay.scene, this.overlay.camera);
    } finally {
      for (const o of hidden) o.visible = false;
    }
  }

  private projected = new Vector3();

  /** A point in the camera's own frame (x right, y up, z towards the viewer: in front of the camera is negative z). */
  cameraSpace(x: number, y: number, z: number): { x: number; y: number; z: number } {
    const cam = this.chase.camera;
    cam.updateMatrixWorld();
    const p = this.projected.set(x, y, z).applyMatrix4(cam.matrixWorldInverse);
    return { x: p.x, y: p.y, z: p.z };
  }

  /** Where a point in the world lands on screen, in CSS pixels; null behind the camera or off screen. */
  screenPoint(x: number, y: number, z: number, width: number, height: number): { x: number; y: number } | null {
    const cam = this.chase.camera;
    cam.updateMatrixWorld();
    const p = this.projected.set(x, y, z).project(cam);
    if (p.z > 1 || p.z < -1 || Math.abs(p.x) > 1.1 || Math.abs(p.y) > 1.1) return null;
    return { x: (p.x * 0.5 + 0.5) * width, y: (1 - (p.y * 0.5 + 0.5)) * height };
  }

  private lightSpace = new Matrix4();
  private tmp = new Vector3();

  private followShadow(x: number, y: number, z: number): void {
    // Keep the shadow box around the car, snapped to shadow-map texels (no shimmer).
    const S = this.sun.shadow.camera.right;
    const texel = (2 * S) / this.sun.shadow.mapSize.x;
    const center = this.tmp.set(x, y, z);
    this.lightSpace.lookAt(new Vector3(0, 0, 0), this.lightDir.clone().negate(), new Vector3(0, 1, 0));
    const inv = this.lightSpace.clone().invert();
    center.applyMatrix4(inv);
    center.x = Math.round(center.x / texel) * texel;
    center.y = Math.round(center.y / texel) * texel;
    center.applyMatrix4(this.lightSpace);
    this.sun.target.position.copy(center);
    this.sun.position.copy(center).addScaledVector(this.lightDir, 500);
  }

  private sunNdc = new Vector3();

  private placeSun(): void {
    const cam = this.chase.camera;
    const u = (this.sky.material as unknown as { uniforms: Record<string, { value: Vector3 | number }> }).uniforms;
    const size = this.renderer.getDrawingBufferSize(new Vector2());
    const p = this.sunNdc.copy(this.sunDir).multiplyScalar(4000).add(cam.position).project(cam);
    const facing = cam.getWorldDirection(new Vector3()).dot(this.sunDir) > 0 && p.z < 1;
    (u.uSunPx.value as Vector3).set((p.x * 0.5 + 0.5) * size.x, (p.y * 0.5 + 0.5) * size.y, facing ? 1 : 0);
    u.uSunR.value = size.y * LIGHT.sunRadius;
  }

  render(f: FrameInfo): void {
    this.time += f.dt;
    this.player.update(f.player, f.dt);
    if (f.orbit) this.chase.orbit(f.player.x, f.player.y, f.player.z, f.player.yaw, this.time);
    else this.chase.update(f.camera, f.dt);
    this.cameraHook?.(f.dt);
    this.showGhost(this.ghost, f.ghost, f.player, f.dt);
    const more = f.ghosts ?? [];
    while (this.extra.length < more.length) {
      const rig = new CarRig(buildCar(this.look, true));
      rig.model.root.traverse((c) => (c.castShadow = false));
      this.scene.add(rig.model.root);
      this.extra.push(rig);
      this.extraGold.push(false);
    }
    // A ghost that's the champion's lap (or stops being one, the laps swapped round) is rebuilt in its colours.
    for (let i = 0; i < more.length; i++) {
      const gold = f.ghostsGold?.[i] ?? false;
      if (gold !== this.extraGold[i]) {
        this.extra[i] = this.swapRig(this.extra[i], true, gold);
        this.extraGold[i] = gold;
      }
    }
    this.extra.forEach((rig, i) => (this.extraOpacity[i] = this.showGhost(rig, more[i] ?? null, f.player, f.dt)));
    this.followShadow(f.player.x, f.player.y, f.player.z);
    this.scenery.crowd.update(this.time, f.excitement);
    this.mods.update(this.time);
    this.sky.position.copy(this.chase.camera.position);
    this.placeSun();

    const r = this.renderer;
    r.info.reset();
    if (this.matte) {
      // The filming mode's mask: the car alone, white on black.
      this.matteMaterial ??= new MeshBasicMaterial({ color: 0xffffff, fog: false });
      const bg = this.scene.background;
      this.scene.background = null;
      this.scene.overrideMaterial = this.matteMaterial;
      r.setClearColor(0x000000, 1);
      r.clear();
      r.render(this.scene, this.chase.camera);
      this.scene.overrideMaterial = null;
      this.scene.background = bg;
      return;
    }
    r.clear();
    r.render(this.scene, this.chase.camera);
    if (this.postEnabled) r.render(this.overlay.scene, this.overlay.camera);
  }
}

/** The game's renderer, drawing `pixelRatio` pixels per screen point (render/sharpness.ts picks it). */
export function createRenderer(canvas: HTMLCanvasElement, pixelRatio: number): WebGLRenderer {
  const r = new WebGLRenderer({ canvas, antialias: true, powerPreference: 'high-performance' });
  r.setPixelRatio(pixelRatio);
  r.outputColorSpace = SRGBColorSpace;
  r.toneMapping = NoToneMapping;
  r.shadowMap.enabled = true;
  r.shadowMap.type = PCFShadowMap;
  r.autoClear = false;
  r.info.autoReset = false;
  return r;
}

/**
 * The browser draws the 3D scene on the processor, not the graphics card (hardware acceleration off, or a driver
 * it won't use): no sharpness makes that smooth. Firefox names the renderer outright; Chrome and Safari say
 * "WebKit WebGL" and name it through an extension (asked only then: Firefox warns about it).
 */
export function drawsInSoftware(r: WebGLRenderer): boolean {
  try {
    const gl = r.getContext();
    let name = String(gl.getParameter(gl.RENDERER) ?? '');
    if (/webkit/i.test(name)) {
      const ext = gl.getExtension('WEBGL_debug_renderer_info');
      if (ext) name = String(gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) ?? '');
    }
    return SOFTWARE_GL.test(name);
  } catch {
    return false;
  }
}

/** Free GPU resources of a world we're replacing (keeps the renderer). */
export function disposeWorld(w: World): void {
  w.scene.traverse((o) => {
    const m = o as unknown as { geometry?: { dispose(): void }; material?: { dispose(): void; map?: { dispose(): void } | null } | { dispose(): void }[] };
    m.geometry?.dispose();
    const mats = Array.isArray(m.material) ? m.material : m.material ? [m.material] : [];
    for (const mat of mats) {
      (mat as { map?: { dispose(): void } | null }).map?.dispose();
      mat.dispose();
    }
  });
}

function hashSeed(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  return (h >>> 0) % 100000;
}


/**
 * Where a setting's sea goes: on the far side of the circuit from its hilltop town, the coast a good way past
 * the circuit's last bit of road that way (so the race never comes near the water), and the water never as
 * high as the road: below the circuit's lowest point, on a day with hills.
 */
function planSea(track: TrackData, plan: SceneryPlan, level: number): SeaPlan {
  const cx = plan.bounds.cx;
  const cy = plan.bounds.cy;
  let dx = cx - plan.town.x;
  let dy = cy - plan.town.y;
  const l = Math.hypot(dx, dy) || 1;
  dx /= l;
  dy /= l;
  let far = -Infinity;
  for (let i = 0; i < track.n; i++) far = Math.max(far, (track.px[i] - cx) * dx + (track.py[i] - cy) * dy);
  let low = Infinity;
  for (let i = 0; i < track.n; i++) low = Math.min(low, track.elev[i]);
  return { dx, dy, cx, cy, coast: far + 130, level: Math.min(level, low + level) };
}
